import * as React from "react";
import { useCallback, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router-dom";
import { useGetCaregiverQuery, useUpdateCaregiverMutation } from "api";
import { isBioErrorResponse, isFetchBaseQueryError } from "api/types";
import { FormikHelpers } from "formik/dist/types";
import { isNil, isNotNil } from "ramda";

import { AffinityForm, FormView } from "affinityForm";
import { Layout } from "affinityUI/structures/layout";
import { UserConfirmation, UserForm } from "components/views/userEdit/";
import { UserInfoType } from "components/views/userEdit/types";
import { userInfoValidationSchema } from "components/views/userEdit/userForm/validationSchema";
import { careGiverConverter, careGiverErrorFieldConverter, careGiverReverseConverter } from "lib/biot/careGiver";

export const EditUserPage: React.FC = () => {
  const { showBoundary: onError } = useErrorBoundary();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [updateCaregiver] = useUpdateCaregiverMutation();
  const { data: caregiverData, isLoading: loading } = useGetCaregiverQuery({
    id: id as string,
  });
  const user = useMemo(() => (caregiverData ? careGiverConverter(caregiverData) : null), [caregiverData]);
  const updateUser = useCallback(
    async (formData: UserInfoType, formikHelpers: FormikHelpers<UserInfoType>) => {
      if (isNil(user)) {
        return;
      }
      try {
        await updateCaregiver({
          id: user.id,
          updateCaregiverRequest: {
            ...careGiverReverseConverter(formData),
          },
        }).unwrap();
        navigate(`/users`);
      } catch (error) {
        if (
          isFetchBaseQueryError(error) &&
          (error.status === 400 || error.status === 409) &&
          isBioErrorResponse(error.data) &&
          isNotNil(error.data.details)
        ) {
          formikHelpers.setFieldError("", error.data.message);

          Object.entries(error.data.details).forEach(([key, value]) =>
            formikHelpers.setFieldError(careGiverErrorFieldConverter(key) ?? key, value as unknown as string)
          );
          navigate(`/users/${user.id}/edit`);
        } else {
          onError(error);
        }
      }
    },
    [navigate, onError, updateCaregiver, user]
  );
  const handleCancel = useCallback(() => {
    navigate(`/users`);
  }, [navigate]);

  return (
    <AffinityForm<UserInfoType>
      validationSchema={userInfoValidationSchema}
      initialData={user}
      confirmHandler={updateUser}
    >
      <FormView
        form={
          <Layout title="Edit user details" loading={loading} backLink="/users" showBackBtn>
            <UserForm
              onCancel={handleCancel}
              invitationAccepted={Boolean(user?.invitationAccepted)}
              isCreate={false}
              currentRoleId={user?.role.id}
            />
          </Layout>
        }
        confirm={
          <Layout title="Confirm and update user details" backLink=".." showBackBtn>
            <UserConfirmation onCancel={handleCancel} />
          </Layout>
        }
      />
    </AffinityForm>
  );
};
